import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@app/context/SessionSettingsContext'

import { useModal } from '@context/ModalsContext'

import { Body1 } from '@ui/Typography'
import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { TableNoResults } from '@ui/v2/Table/Table'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'

import { useIBReferralCodes } from '@hooks/useIBReferralCodes'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'

import { PromoPreviewModal } from './PromoPreviewModal/PromoPreview.modal'
import { ReferralMaterialCard } from './ReferralMaterialCard'
import { useReferralMaterialsWithFilters } from './hooks/useReferralMaterialsWithFilters'

import styles from './ReferralMaterialsPage.module.scss'

export const ReferralMaterialsPage: FC = () => {
  const { t } = useTranslation()
  const [isFilterVisible, setIsFilterVisible] = useState(false)

  const {
    query: referralMaterialQuery,
    campaigns,
    categories,
    languages,
    sizes,
    search,
    setSearch,
    isLoading,
  } = useReferralMaterialsWithFilters()

  const locale = useSessionLanguage()
  const { mtCode, referralCodes, isLoading: isReferralCodesLoading } = useIBReferralCodes()
  const previewModal = useModal(PromoPreviewModal)

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    referralMaterialQuery.setPageQuery?.({
      ...referralMaterialQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const isAnyFilterActive = useMemo(
    () => Object.values(search || {}).some((value) => !!value),
    [search]
  )

  return (
    <div>
      <PageSection
        title={t('Referral Materials.Referral Materials')}
        id={ScrollToIds.ReferralMaterialsHeader}
        onFilterClick={() => setIsFilterVisible((prevState) => !prevState)}
      />
      <TableFilter
        isVisible={isFilterVisible}
        filterOptions={[
          FilterOptions.CampaignId,
          FilterOptions.CategoryId,
          FilterOptions.LanguageId,
          FilterOptions.SizeId,
        ]}
        campaignSearchProps={{
          options: campaigns,
        }}
        categoriesSearchProps={{
          options: categories,
        }}
        languagesSearchProps={{
          options: languages,
        }}
        sizesSearchProps={{
          options: sizes,
        }}
        searchFilters={search}
        setSearchFilters={(searchFilters) => {
          if (!searchFilters) {
            return
          }

          setSearch(searchFilters)
          referralMaterialQuery.setPageQuery?.({
            ...referralMaterialQuery.pageQuery,
            pageIndex: 1,
          })
        }}
      />

      <Loading isLoading={isLoading || isReferralCodesLoading} showLoadingIcon>
        {referralMaterialQuery.data.length === 0 ? (
          <NoReferralMaterials isAnyFilterActive={isAnyFilterActive} />
        ) : (
          <>
            <div className={styles.referralMaterialCards}>
              {referralMaterialQuery.data.map((referralMaterial) => (
                <ReferralMaterialCard
                  referralMaterial={referralMaterial}
                  caLang={locale}
                  openReferralMaterialModal={(referralMaterialId, lang, query) => {
                    previewModal.open({
                      mtCode: mtCode,
                      referralCodes: referralCodes,
                      referralMaterialId,
                      language: lang,
                      query,
                    })
                  }}
                  searchQuery={search}
                  key={referralMaterial.imagePath}
                />
              ))}
            </div>

            {referralMaterialQuery.meta && referralMaterialQuery.hasInitialResults && (
              <Pagination
                scrollToHeaderId={ScrollToIds.ReferralMaterialsHeader}
                maxPageSize={100}
                pageData={referralMaterialQuery.meta}
                isLoading={referralMaterialQuery.isLoading}
                onPageChanged={handlePageChange}
                isPageSizeDivisibleByThree
              />
            )}
          </>
        )}
      </Loading>
    </div>
  )
}

interface NoReferralMaterialsProps {
  isAnyFilterActive?: boolean
}

const NoReferralMaterials: FC<NoReferralMaterialsProps> = (props) => {
  const { isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <TableNoResults
      title={
        isAnyFilterActive
          ? t('No results')
          : t('Referral Materials.No referral materials to display')
      }
      content={
        isAnyFilterActive ? <Body1>{t('IB.RebatesPage.Please, adjust your filters')}</Body1> : null
      }
    />
  )
}
